import { createContext, useContext } from "react";
import type { FeatureRegistryStore } from "./feature-registry/feature-registry-store";
import type { LocaleStore } from "./locale-store";
import type { NotificationStore } from "./notification-store";
import type { SessionStore } from "./session-store";
import type { ShellStatusStore } from "./shell-status-store";
import type { ThemeStore } from "./theme-store";
import type { WorkspaceStore } from "./workspace/workspace-store";
import type { ApiClient } from "../platform/api-client";
import type { MissionSelectionStore } from "../features/operations/mission-selection-store";
import type { OperationalContextStore } from "../features/operations/operational-context-store";

export interface AppStores {
  api: ApiClient;
  features: FeatureRegistryStore;
  locale: LocaleStore;
  /**
   * App-level mission selection bus (see `features/operations/mission-selection-store.ts`).
   * Lives here — not as local state in `operations-route.tsx` — so a selection
   * made on `/operations` survives navigating to a different route (e.g. the
   * `/modules/resource-requests` form that pre-fills from it).
   */
  missionSelection: MissionSelectionStore;
  notifications: NotificationStore;
  operationalContext: OperationalContextStore;
  session: SessionStore;
  status: ShellStatusStore;
  theme: ThemeStore;
  workspace: WorkspaceStore;
}

export const AppStoreContext = createContext<AppStores | undefined>(undefined);

export function useAppStores(): AppStores {
  const stores = useContext(AppStoreContext);

  if (!stores) {
    throw new Error("App stores are unavailable outside AppProviders.");
  }

  return stores;
}
